import {
  Bell,
  Database,
  FileText,
  Lock,
  Mail,
  MapPin,
  Phone,
  Shield,
  Trash2,
  Users,
} from "lucide-react";
import React from "react";

const PrivacyPolicy: React.FC = () => {
  const sections = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Information Collection and Use",
      content: [
        {
          subtitle: "a. Personal Information",
          text: "We may collect personal information that you voluntarily provide when using the App, including:",
          list: [
            "Full name",
            "Email address",
            "Phone number",
            "Profile details",
            "Login credentials",
          ],
        },
        {
          subtitle: "b. Vehicle & Fleet Data",
          list: [
            "Vehicle identification numbers (VIN)",
            "Vehicle status",
            "Maintenance records",
            "Fuel consumption data",
            "Driver assignments",
          ],
        },
        {
          subtitle: "c. Location Data",
          list: [
            "Real-time GPS location of vehicles",
            "Driver route data",
            "Delivery or trip history",
          ],
        },
      ],
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "How We Use Your Information",
      text: "We use collected data to:",
      list: [
        "Manage and monitor your fleet",
        "Assign drivers and track trips",
        "Improve operational efficiency",
        "Provide analytics and reports",
        "Send notifications, alerts, or updates",
        "Ensure security",
        "Improve and customize user experience",
      ],
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Location Information",
      text: "Fleet Pro does not gather precise information about the location of your mobile device. Fleet Pro may use the information you provided to contact you from time to time to provide you with important information and required notices.",
    },
    {
      icon: <Trash2 className="w-6 h-6" />,
      title: "Opt-Out Rights",
      text: "At Fleet Pro, you can stop all collection of information by the Application easily by uninstalling it. You may use the standard uninstall processes as may be available as part of your mobile device or via the mobile application marketplace or network.",
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Retention Policy",
      text: "Here at Fleet Pro, we will retain User Provided data for as long as you use the Application and for a reasonable time thereafter. If you'd like us to delete User Provided Data that you have provided via the Application, please contact us at fleetpro18@gmail.com and we will respond in a reasonable time.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Children",
      text: "Fleet Pro does not use the Application to knowingly solicit data from or market to children under the age of 18. We do not knowingly collect personally identifiable information from children under 18 years of age. In the case we discover that a child under 18 has provided personal information, Fleet Pro will immediately delete this from our server.",
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Security",
      text: "At Fleet Pro we are concerned about safeguarding the confidentiality of your information. We provide physical, electronic, and procedural safeguards to protect information we process and maintain.",
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Changes",
      text: "This Privacy Policy may be updated from time to time for any reason. We will notify you of any changes to the Privacy Policy by updating this page with the new Privacy Policy. You are advised to consult this Privacy Policy regularly for any changes, as continued use is deemed approval of all changes.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-28">
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                Privacy Policy
              </h2>
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                At Fleet Pro we collect, use, and safeguard your information
                when you use our fleet management application system and related
                services. Please read this policy carefully to understand our
                views and practices regarding your personal data and how we will
                treat it.
              </p>
            </div>
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md p-6 sm:p-8 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-100 p-2.5 rounded-lg flex-shrink-0">
                  {section.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 leading-tight">
                  {section.title}
                </h3>
              </div>

              {section.text && (
                <p className="text-gray-700 mb-4 leading-relaxed ml-0 sm:ml-14">
                  {section.text}
                </p>
              )}

              {section.list && (
                <ul className="space-y-2 ml-0 sm:ml-14">
                  {section.list.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-gray-700"
                    >
                      <span className="text-blue-600 mt-1.5 flex-shrink-0">
                        •
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.content && (
                <div className="space-y-6 ml-0 sm:ml-14">
                  {section.content.map((item, i) => (
                    <div key={i}>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {item.subtitle}
                      </h4>
                      {item.text && (
                        <p className="text-gray-700 mb-3">{item.text}</p>
                      )}
                      {item.list && (
                        <ul className="space-y-2">
                          {item.list.map((listItem, j) => (
                            <li
                              key={j}
                              className="flex items-start gap-2 text-gray-700"
                            >
                              <span className="text-blue-600 mt-1.5 flex-shrink-0">
                                •
                              </span>
                              <span>{listItem}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Consent Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 sm:p-8 mt-8 text-white">
          <h3 className="text-2xl font-bold mb-3">Your Consent</h3>
          <p className="text-blue-50 leading-relaxed">
            By using the Application, you are consenting to the processing of
            your information as set forth in this Privacy Policy now and as
            amended by us.
          </p>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h3>
          <p className="text-gray-700 mb-6 leading-relaxed">
            If you have any questions regarding privacy while using the
            Application, or have questions about our practices, please contact
            us:
          </p>
          <div className="space-y-4">
            <a
              href="mailto:fleetpro18@gmail.com"
              className="flex items-center gap-3 text-blue-600 hover:text-blue-700 transition-colors group"
            >
              <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                <Mail className="w-5 h-5" />
              </div>
              <span className="font-medium">fleetpro18@gmail.com</span>
            </a>
            <a
              href="tel:09099843428"
              className="flex items-center gap-3 text-blue-600 hover:text-blue-700 transition-colors group"
            >
              <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                <Phone className="w-5 h-5" />
              </div>
              <span className="font-medium">09099843428</span>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
